import { ProjectFormData } from './project-form-data.interface';

/**
 * Interfaz que representa el formato de datos que espera el backend
 */
export interface BackendProjectData {
    id?: number;
    name: string;
    code: string;
    pep?: string;
    aprobado: boolean;
    active: boolean;
    visible: boolean;
    force_comments: boolean;
    normal_hours: boolean;
    extra_hours: boolean;
    tipo?: string;
    id_cliente?: string;
    origen?: string;
    moneda?: string;
    fecha_inicio?: string;
    fecha_termino?: string;
    fecha_estimada_termino?: string;
    id_jefe_proyecto?: string;
    id_control_proyecto?: string;
    id_control_documental?: string;
    hh_estimadas?: number;
    parent_id?: number;
    level: number;
    codigo_padre?: string;
    codigo_glosa?: string;
    codigo_lista?: string;
}

/**
 * Convierte ProjectFormData (frontend) a BackendProjectData (backend)
 */
export function mapFrontendToBackend(frontendData: ProjectFormData): any {
    // Enviar TODOS los campos siempre (usar null en vez de undefined para campos vacíos)
    // Esto permite que el backend pueda limpiar valores (ej: eliminar padre, limpiar fecha)
    const backendData: any = {
        id: frontendData.id ? parseInt(frontendData.id) : undefined,
        name: frontendData.proyecto,
        code: frontendData.codigo,
        pep: frontendData.pep || null,
        aprobado: frontendData.aprobado || false,
        active: frontendData.activo,
        visible: frontendData.visible !== false,
        force_comments: frontendData.requiereObservacion,
        normal_hours: frontendData.horasNormales,
        extra_hours: frontendData.horasExtras,
        tipo: frontendData.tipo || null,
        id_cliente: frontendData.id_cliente || null,
        origen: frontendData.origen || null,
        moneda: frontendData.moneda || null,
        fecha_inicio: frontendData.fechaInicio || null,
        fecha_termino: frontendData.fechaTermino || null,
        fecha_estimada_termino: frontendData.fechaEstimadaTermino || null,
        id_jefe_proyecto: frontendData.id_jefeProyecto || null,
        id_control_proyecto: frontendData.id_controlProyecto || null,
        id_control_documental: frontendData.id_controlDocumental || null,
        hh_estimadas: frontendData.HHEstimadas || null,
        parent_id: frontendData.parentId ? parseInt(frontendData.parentId) : null,
        level: frontendData.level,
        codigo_padre: frontendData.codigoPadre ?? '',
        codigo_glosa: frontendData.codigoGlosa ?? '',
        codigo_lista: frontendData.codigoLista ?? ''
    };

    // Solo eliminar id si no existe (creación)
    if (backendData.id === undefined) {
        delete backendData.id;
    }

    return backendData;
}

/**
 * Convierte BackendProjectData (backend) a ProjectFormData (frontend)
 * Maneja tanto los tags JSON como los nombres de columnas de la BD
 */
export function mapBackendToFrontend(backendData: any): ProjectFormData {
    // El backend puede devolver datos con tags JSON o nombres de columnas
    // Intentar ambos formatos para mayor compatibilidad
    return {
        id: backendData.id?.toString() || backendData.ID?.toString(),
        proyecto: backendData.name || backendData.Name || '',
        codigo: backendData.code || backendData.Code || '',
        pep: backendData.pep || backendData.PEP || undefined,
        aprobado: backendData.aprobado || backendData.Aprobado || false,
        activo: backendData.active !== false && backendData.Active !== false,
        visible: backendData.visible !== false && backendData.Visible !== false,
        requiereObservacion: backendData.force_comments || backendData.ForceComments || false,
        horasExtras: backendData.extra_hours !== false && backendData.ExtraHours !== false,
        horasNormales: backendData.normal_hours !== false && backendData.NormalHours !== false,
        tipo: backendData.tipo?.toString() || backendData.Tipo?.toString() || '',
        id_cliente: backendData.id_cliente?.toString() || backendData.IdCliente?.toString() || '',
        origen: backendData.origen?.toString() || backendData.Origen?.toString() || '',
        moneda: backendData.moneda?.toString() || backendData.Moneda?.toString() || undefined,
        fechaInicio: backendData.fecha_inicio || backendData.FechaInicio || undefined,
        fechaTermino: backendData.fecha_termino || backendData.FechaTermino || undefined,
        fechaEstimadaTermino: backendData.fecha_estimada_termino || backendData.FechaEstimadaTermino || undefined,
        id_jefeProyecto: backendData.id_jefe_proyecto || backendData.IdJefeProyecto || undefined,
        id_controlProyecto: backendData.id_control_proyecto || backendData.IdControlProyecto || undefined,
        id_controlDocumental: backendData.id_control_documental || backendData.IdControlDocumental || undefined,
        HHEstimadas: backendData.hh_estimadas || backendData.HHEstimadas || undefined,
        parentId: (backendData.parent_id || backendData.ParentID)?.toString(),
        level: backendData.level || backendData.Level || 0,
        // IMPORTANTE: Para los campos de código separados, usar verificación explícita
        // para manejar strings vacíos correctamente (no convertirlos a undefined)
        codigoPadre: (backendData.codigo_padre !== undefined && backendData.codigo_padre !== null) 
            ? backendData.codigo_padre 
            : ((backendData.CodigoPadre !== undefined && backendData.CodigoPadre !== null) 
                ? backendData.CodigoPadre 
                : undefined),
        codigoGlosa: (backendData.codigo_glosa !== undefined && backendData.codigo_glosa !== null) 
            ? backendData.codigo_glosa 
            : ((backendData.CodigoGlosa !== undefined && backendData.CodigoGlosa !== null) 
                ? backendData.CodigoGlosa 
                : undefined),
        codigoLista: (backendData.codigo_lista !== undefined && backendData.codigo_lista !== null) 
            ? backendData.codigo_lista 
            : ((backendData.CodigoLista !== undefined && backendData.CodigoLista !== null) 
                ? backendData.CodigoLista 
                : undefined)
    };
}

